import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { ShoppingBag, CheckCircle, Ticket } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';

const SocialProof = () => {
  const [purchases, setPurchases] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const response = await axios.get(`${API}/public/recent-purchases`);
        if (response.data && response.data.length > 0) {
          setPurchases(response.data);
        }
      } catch (error) {
        console.error('Error fetching social proof data:', error);
      }
    };

    fetchPurchases();
    // Refrescar cada 5 minutos
    const intervalFetch = setInterval(fetchPurchases, 5 * 60 * 1000);
    return () => clearInterval(intervalFetch);
  }, []);

  useEffect(() => {
    if (purchases.length === 0) return;

    let progressTimer;
    let rotationTimer;

    const startRotation = () => {
      // Reiniciar progreso
      setProgress(0);
      setVisible(true);

      // Iniciar barra de progreso (coincide con el tiempo de visibilidad)
      const duration = 8000; // 8 segundos visible
      const step = 100;
      const increment = (step / duration) * 100;

      progressTimer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressTimer);
            return 100;
          }
          return prev + increment;
        });
      }, step);

      // Programar ocultamiento
      rotationTimer = setTimeout(() => {
        setVisible(false);
        // Esperar a que se oculte antes de cambiar al siguiente
        setTimeout(() => {
          setCurrentIndex(prev => (prev + 1) % purchases.length);
          // Reiniciar el ciclo después de un intervalo de pausa
          setTimeout(startRotation, 4000); // 4 segundos de pausa entre notificaciones
        }, 1000);
      }, duration);
    };

    // Inicio diferido inicial
    const initialTimer = setTimeout(startRotation, 3000);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(rotationTimer);
      clearTimeout(initialTimer);
    };
  }, [purchases]);

  if (purchases.length === 0 || !purchases[currentIndex]) return null;
  const current = purchases[currentIndex];

  return (
    <div 
      className={`fixed bottom-6 left-6 z-[100] transition-all duration-1000 ease-[cubic-bezier(0.23,1,0.32,1)] transform ${
        visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95 pointer-events-none'
      } hidden sm:block`}
    >
      <div className="relative group">
        {/* Glow efecto premium */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
        
        <div className="relative bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl border border-slate-200/50 dark:border-white/10 rounded-2xl shadow-2xl p-4 flex items-center gap-4 w-[280px] sm:w-[320px] overflow-hidden">
          
          {/* Icono Principal */}
          <div className="flex-shrink-0 relative">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 flex items-center justify-center border border-orange-500/20">
              <ShoppingBag className="w-6 h-6 text-orange-500" />
            </div>
            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5 border-2 border-white dark:border-slate-950">
              <CheckCircle className="w-2.5 h-2.5 text-white" />
            </div>
          </div>
          
          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-0.5">
              ¡Compra reciente de <span className="font-bold text-slate-900 dark:text-white">{current?.nombre || "Usuario"}</span>!
            </p>
            <p className="text-sm font-black text-slate-900 dark:text-white truncate leading-tight">
              {current?.sorteo || "Sorteo"}
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="inline-flex items-center gap-1 text-[10px] font-black text-orange-500 bg-orange-500/10 px-1.5 py-0.5 rounded-md uppercase tracking-wide">
                 <Ticket className="w-2.5 h-2.5" /> {current?.cantidad || 1} {current?.cantidad === 1 ? 'BOLETO' : 'BOLETOS'}
              </span>
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 italic">
                hace {current?.fecha ? formatDistanceToNow(new Date(current.fecha), { locale: es }) : "instantes"}
              </span>
            </div>
          </div>

          {/* Barra de Progreso Discreta */}
          <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-100 ease-linear" style={{ width: `${progress}%` }}></div>
        </div>
      </div>
    </div>
  );
};

export default SocialProof;
